import { Button } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAtomValue, useSetAtom } from 'jotai';
import ConfirmDialog from '../../common/components/ConfirmDialog';
import { GetCompanyUsersDocument, User_Role_Enum } from '../../queries/__generated__/getCompanyUsers.generated';
import { commonSnackbarPropsAtom } from '../../common/state/state';
import { currentContextAtom } from '../../state';
import { useDeleteRoleAssignmentMutation } from '../../mutations/__generated__/deleteRoleAssignment.generated';
import { UserRoleEnum } from '../../../../../packages/graphql/packages/graphql/types';

export interface DeleteUserButtonProps {
  userId: string;
  fullName: string;
  role: User_Role_Enum;
  isAdmin: boolean;
}

export default function DeleteUserButton({ userId, fullName, role, isAdmin }: DeleteUserButtonProps): React.JSX.Element {
  const { t } = useTranslation(['common', 'company']);
  const { companyId } = useAtomValue(currentContextAtom);
  const setCommonSnackbarProps = useSetAtom(commonSnackbarPropsAtom);
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [deleteRole] = useDeleteRoleAssignmentMutation({});

  const handleDeleteConfirm = async (confirm: boolean): Promise<void> => {
    if (confirm) {
      const deleteRolePromises = [
        deleteRole({
          variables: {
            companyId,
            userId,
            role: role as UserRoleEnum,
          },
          refetchQueries: isAdmin ? [] : [GetCompanyUsersDocument],
        }),
      ];

      if (isAdmin) {
        deleteRolePromises.push(
          deleteRole({
            variables: {
              companyId,
              userId,
              role: UserRoleEnum.CompanyAdmin,
            },
            refetchQueries: [GetCompanyUsersDocument],
          })
        );
      }

      const results = await Promise.all(deleteRolePromises);

      if (!results.some(({ errors }) => !!errors?.length)) {
        setCommonSnackbarProps({
          label: t('company:account_deleted_successfully'),
          snackbarProps: {
            open: true,
          },
        });
      } else {
        setCommonSnackbarProps({
          label: t('error:an_error_occured'),
          snackbarProps: {
            open: true,
          },
          alertProps: {
            severity: 'error',
          },
        });
      }
    }

    setIsConfirmDialogOpen(false);
  };

  return (
    <>
      <Button
        variant="text"
        color="error"
        startIcon={<DeleteOutlineIcon />}
        onClick={() => setIsConfirmDialogOpen(true)}
      >
        {t('common:delete')}
      </Button>
      <ConfirmDialog
        open={isConfirmDialogOpen}
        onClose={handleDeleteConfirm}
        cancelLabel={t('common:cancel')}
        confirmLabel={t('company:delete_user_dialog.confirm_label')}
        confirmColor="error"
        title={t('company:delete_user_dialog.title')}
        subTitle={t('company:delete_user_dialog.content', { fullName })}
      />
    </>
  )
}
